import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import "./Deductions.scss";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import IconButton from "../../../components/IconButton/IconButton";
import GeneralTable from "../../../components/Table/GeneralTable";

function Deductions() {
  const [deductions, setDeductions] = useState([]);
  const [newDeduction, setNewDeduction] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingDeduction, setEditingDeduction] = useState(null);

  useEffect(() => {
    fetchDeductions();
  }, []);

  const fetchDeductions = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/deductions`,
      );
      const { data } = response.data;
      setDeductions(data || []);
    } catch (err) {
      console.error("Error fetching deductions:", err);
      setError("Failed to fetch deductions");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDeduction = async (e) => {
    e.preventDefault();
    if (!newDeduction.trim()) {
      setError("Deduction name cannot be empty");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = { deductionName: newDeduction };
      await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/deductions`,
        payload,
      );
      setNewDeduction(""); // Reset input field after submission
      fetchDeductions(); // Re-fetch deductions after creation
    } catch (err) {
      console.error("Error creating deduction:", err);
      setError("Failed to create deduction");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDeduction = (deduction) => {
    setEditingDeduction(deduction);
  };

  const handleSaveEdit = async () => {
    if (!editingDeduction.deductionName.trim()) {
      setError("Deduction name cannot be empty");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        otherDeductionId: editingDeduction.otherDeductionId,
        deductionName: editingDeduction.deductionName,
      };
      await axiosInstance.put(
        `${process.env.REACT_APP_API_BASE_URL}/deductions`,
        payload,
      );
      setEditingDeduction(null);
      fetchDeductions(); // Re-fetch deductions after edit
    } catch (err) {
      console.error("Error editing deduction:", err);
      setError(
        "Failed to edit deduction: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  // Define columns for GeneralTable
  const columns = [
    {
      header: "Name",
      accessor: "deductionName",
    },
    {
      header: "Actions",
      accessor: "actions",
    },
  ];

  // Transform data to include actions
  const tableData = deductions.map((deduction) => ({
    ...deduction,
    deductionName:
      editingDeduction?.otherDeductionId === deduction.otherDeductionId ? (
        <input
          type="text"
          value={editingDeduction.deductionName || ""}
          onChange={(e) =>
            setEditingDeduction({
              ...editingDeduction,
              deductionName: e.target.value,
            })
          }
          style={{ width: "100%", padding: "4px 8px" }}
        />
      ) : (
        deduction.deductionName || "Unnamed Deduction"
      ),
    actions:
      editingDeduction?.otherDeductionId === deduction.otherDeductionId ? (
        <IconButton
          onClick={handleSaveEdit}
          className="icon-button--add"
          icon={"material-symbols-light:add"}
        />
      ) : (
        <IconButton
          className="icon-button--edit"
          onClick={() => handleEditDeduction(deduction)}
          icon="iconamoon:edit-light"
          title="Edit Deduction"
        />
      ),
  }));

  return (
    <div className="deductions-container">
      <h1 className="display6">Deductions</h1>

      {loading && <LoadingScreen />}
      {error && <p className="error-message">{error}</p>}

      {/* Create Deduction Form */}
      <form onSubmit={handleCreateDeduction}>
        <label htmlFor="newDeduction">New Deduction Name:</label>

        <div className="input-group">
          <input
            type="text"
            id="newDeduction"
            value={newDeduction}
            onChange={(e) => setNewDeduction(e.target.value)}
            placeholder="Enter deduction name"
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? "Submitting..." : "Submit"}
          </button>
        </div>
      </form>

      {/* Deductions Table using GeneralTable */}
      <GeneralTable
        columns={columns}
        data={tableData}
        title="Deductions List"
      />
    </div>
  );
}

export default Deductions;
